import React, { useMemo } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import MarkdownRender from 'LuxKit/MarkdownRender'
import ExperienceMap from 'components/Experiences/ExperienceMap'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import Caption from 'LuxKit/Typography/Caption'
import Typography from 'LuxKit/Typography/Typography'
import Group from 'components/utils/Group'

const STYLE = {
  height: 500,
  width: '100%',
  flexGrow: 1,
}

interface Props {
  experienceView: App.ExperienceBookingView
  pickupPoint?: App.ExperiencePickupPoint
  selectedLocation?: App.ExperienceRedemptionLocation
  onZoomChanged?: (zoom: boolean) => void
  distanceFromProperty?: number | null
}

function ExperienceDetailsAboutLocationMap(props: Props) {
  const { experienceView, pickupPoint, selectedLocation, onZoomChanged, distanceFromProperty } = props
  const intl = useIntl()

  const whereText = useMemo(() => {
    if (selectedLocation?.name) {
      return selectedLocation?.name
    }
    // if only one redemption location, show the default location name
    return (experienceView.originalExperience?.redemptionLocations?.length > 1) ?
        intl.formatMessage({
          id: 'S/tdo6bqf/',
          defaultMessage: 'Multiple Locations',
          description: 'Label shown when experience has multiple redemption locations on map',
        }) :
      experienceView.originalExperience?.copy?.whereText
  }, [experienceView, selectedLocation, intl])

  const experienceMarker = useMemo<App.MapMarker | undefined>(() => {
    const marker: App.MapMarker = {
      id: 'experience',
      type: 'moments',
      latitude: experienceView.originalExperience.location.latitude,
      longitude: experienceView.originalExperience.location.longitude,
    }

    if (!marker.latitude || !marker.longitude) { return undefined }

    if (selectedLocation) {
      marker.latitude = selectedLocation.latitude
      marker.longitude = selectedLocation.longitude
    }

    return marker
  }, [experienceView, selectedLocation])

  const locationMapMarkerList = useMemo<Array<App.MapMarker>>(() => {
    const markers = [...experienceView.interestingLocations]

    if (experienceView.originalExperience.redemptionLocations?.length > 1) {
      markers.push(...experienceView.originalExperience.redemptionLocations.map(location => ({
        id: location.id,
        type: 'see-and-do',
        latitude: location.latitude,
        longitude: location.longitude,
      }) as App.MapMarker))
    }
    return markers
  }, [experienceView])

  const pickupPointMarker = useMemo<App.MapMarker | undefined>(() => {
    if (pickupPoint?.latitude && pickupPoint.longitude) {
      return {
        id: 'pickupPoint',
        type: 'see-and-do',
        latitude: pickupPoint.latitude,
        longitude: pickupPoint.longitude,
      }
    }
  }, [pickupPoint])

  return (
    <VerticalSpacer gap={16}>
      {whereText &&
        <Group direction="horizontal" gap={8} verticalAlign="center">
          <MarkdownRender fontWeight="bold" content={whereText} />
          {distanceFromProperty && <Typography wrap="pretty">
            <Caption as="span" variant="large" colour="neutral-two" weight="normal">
              <FormattedMessage
                id="C/2Le5x9iS"
                defaultMessage="{distance} km from hotel"
                description="Distance from hotel for an experience, e.g. '2.5 km from hotel'"
                values={{
                  distance: <FormatDecimal maximumFractionDigits={distanceFromProperty > 1 ? 0 : 1} value={distanceFromProperty} />,
                }}
              />
            </Caption>
          </Typography>}
        </Group>}
      <ExperienceMap
        title={intl.formatMessage(
          {
            id: 'Q3QO/M3HD7',
            defaultMessage: 'Experience: {title}',
            description: 'Map marker title for experience location for the experience',
          },
          { title: experienceView.title },
        )}
        googleMapId="le-map"
        zoomLevel={17}
        primaryMapMarker={experienceMarker}
        pickupMapMarker={pickupPointMarker}
        locationMapMarkerList={locationMapMarkerList}
        style={STYLE}
        onZoomChanged={onZoomChanged}
      />
      {experienceView.originalExperience?.copy.meetingPoint && <div>
        <BodyText variant="large" weight="bold">{intl.formatMessage({
          id: 'quYPXos7Lx',
          defaultMessage: 'Meeting point',
          description: 'Label for meeting point on experience location map',
        })}
        </BodyText>
        <MarkdownRender content={experienceView.originalExperience.copy.meetingPoint} />
      </div>}
    </VerticalSpacer>
  )
}

export default ExperienceDetailsAboutLocationMap
